import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { Activity as ActivityIcon, FileText, Loader2, Mail, Phone, Users } from 'lucide-react';
import { useCreate, useGetIdentity, useGetList, useNotify } from 'ra-core';
import { useState } from 'react';

interface ActivityTimelineProps {
    resource: string;
    recordId: string | number;
}

const icons: Record<string, any> = {
    call: Phone,
    email: Mail,
    meeting: Users,
    note: FileText,
    status_change: ActivityIcon
};

const colors: Record<string, string> = {
    call: 'bg-emerald-50 text-emerald-600 border-emerald-250 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-900/50',
    email: 'bg-sky-50 text-sky-600 border-sky-250 dark:bg-sky-950/30 dark:text-sky-400 dark:border-sky-900/50',
    meeting: 'bg-purple-50 text-purple-600 border-purple-250 dark:bg-purple-950/30 dark:text-purple-400 dark:border-purple-900/50',
    note: 'bg-amber-50 text-amber-600 border-amber-250 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-900/50',
    status_change: 'bg-muted text-muted-foreground border-border dark:bg-slate-900/30 dark:text-muted-foreground dark:border-slate-800'
};

export const ActivityTimeline = ({ resource, recordId }: ActivityTimelineProps) => {
    const notify = useNotify();
    const { identity } = useGetIdentity();
    
    const [create, { isPending: isCreating }] = useCreate();
    
    const [note, setNote] = useState('');
    const [type, setType] = useState('note');
    
    const { data: activities, isPending: isLoading, refetch } = useGetList('activities', {
        pagination: { page: 1, perPage: 20 },
        sort: { field: 'created_at', order: 'DESC' },
        filter: { linked_resource: resource, linked_id: recordId }
    });
    
    const handleAdd = async () => {
        if (!note.trim()) return;
        
        try {
            await create('activities', {
                data: {
                    type,
                    note,
                    linked_resource: resource,
                    linked_id: recordId,
                    created_by: identity?.id
                }
            }, {
                onSuccess: () => {
                    setNote('');
                    refetch();
                    notify('Activity added', { type: 'success' });
                }
            });
        } catch (error: any) {
            notify(`Error: ${error.message}`, { type: 'error' });
        }
    };

    const activityTypes = [
        { id: 'note', label: 'Note', icon: FileText },
        { id: 'call', label: 'Call', icon: Phone },
        { id: 'email', label: 'Email', icon: Mail },
        { id: 'meeting', label: 'Meeting', icon: Users }
    ];

    return (
        <Card className="h-full flex flex-col shadow-sm rounded-2xl border-border dark:border-slate-800 py-0 gap-0 overflow-hidden">
            <CardHeader className="pb-3 border-b bg-muted/50 dark:bg-slate-900/30 px-6 pt-5">
                <div className="flex items-center gap-2">
                    <ActivityIcon className="w-5 h-5 text-muted-foreground" />
                    <CardTitle className="text-lg font-semibold tracking-tight">Activity Timeline</CardTitle>
                </div>
            </CardHeader>
            <CardContent className="p-6 flex-1 flex flex-col min-h-0">
                {/* Input Area */}
                <div className="space-y-3.5 mb-4 bg-muted/60 dark:bg-slate-900/30 p-3.5 rounded-xl border border-border/50 dark:border-slate-800/50 shrink-0 animate-fade-in">
                    <div className="flex flex-wrap gap-2">
                        {activityTypes.map((t) => {
                            const Icon = t.icon;
                            const isActive = type === t.id;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setType(t.id)}
                                    className={cn(
                                        "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border cursor-pointer select-none",
                                        isActive 
                                            ? "bg-primary text-white border-primary shadow-sm" 
                                            : "bg-card dark:bg-slate-950 hover:bg-muted dark:hover:bg-slate-900 text-muted-foreground dark:text-slate-300 border-border dark:border-slate-800"
                                    )}
                                >
                                    <Icon className="w-3.5 h-3.5" />
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                    <textarea 
                        className="w-full text-sm border border-slate-250 dark:border-slate-800 rounded-lg p-3 min-h-[70px] max-h-[100px] bg-card dark:bg-slate-950 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all resize-none shadow-sm"
                        placeholder="Log an activity or note..."
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />
                    <div className="flex justify-end">
                        <Button 
                            size="sm" 
                            onClick={handleAdd} 
                            disabled={!note.trim() || isCreating || isLoading}
                            className="bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg shadow-sm cursor-pointer px-4"
                        >
                            {isCreating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            Log Activity
                        </Button>
                    </div>
                </div>

                {/* Timeline */}
                <div className="flex-1 overflow-y-auto space-y-4 pr-2 pb-8 min-h-0 scrollbar-thin">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : activities && activities.length > 0 ? (
                        <div className="relative border-l border-border/60 dark:border-slate-800/60 ml-4 space-y-6 pb-2">
                            {activities.map((act) => {
                                const Icon = icons[act.type] || ActivityIcon;
                                const colorClass = colors[act.type] || colors['note'];
                                return (
                                    <div key={act.id} className="relative pl-8">
                                        <div className={cn(
                                            "absolute left-0 top-1.5 -translate-x-1/2 p-2 rounded-full border-2 border-white dark:border-slate-900 shadow-sm",
                                            colorClass.split(' ').slice(0, 3).join(' ')
                                        )}>
                                            <Icon className="w-3.5 h-3.5" />
                                        </div>
                                        <div className="bg-card dark:bg-slate-900/40 border border-border/60 dark:border-slate-800/60 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-300">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className={cn(
                                                    "text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border",
                                                    colorClass
                                                )}>
                                                    {act.type.replace('_', ' ')}
                                                </span>
                                                <span className="text-[10px] text-muted-foreground dark:text-muted-foreground font-medium">
                                                    {formatDistanceToNow(new Date(act.created_at), { addSuffix: true })}
                                                </span>
                                            </div>
                                            <div className="text-sm text-foreground dark:text-slate-200 whitespace-pre-wrap leading-relaxed">{act.note}</div>
                                            {act.sales && act.sales.first_name && (
                                                <div className="text-[10px] text-muted-foreground dark:text-muted-foreground mt-3 pt-2 border-t border-border dark:border-slate-850 flex items-center gap-1.5">
                                                    <div className="w-4 h-4 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-[8px] uppercase">
                                                        {act.sales.first_name.charAt(0)}
                                                    </div>
                                                    <span>By {act.sales.first_name} {act.sales.last_name}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-10 px-4 bg-muted/50 dark:bg-slate-900/10 rounded-xl border border-dashed border-border dark:border-slate-800 text-center">
                            <div className="w-10 h-10 rounded-full bg-muted dark:bg-slate-900 flex items-center justify-center mb-2.5 text-muted-foreground dark:text-muted-foreground">
                                <ActivityIcon className="w-5 h-5" />
                            </div>
                            <h4 className="text-xs font-semibold text-foreground dark:text-slate-300">No activities logged yet</h4>
                            <p className="text-[10px] text-muted-foreground dark:text-muted-foreground max-w-[180px] mt-1">
                                Log notes, calls, or meetings to keep track of progress.
                            </p>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};
